import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiServices from "../../services/apiServices"; // Assuming this is your API service utility

const BASE_URL = "/purchase"; // Update this with your actual API endpoint

// Async Thunks

// Create a new purchase
export const createPurchase = createAsyncThunk(
  "purchases/createPurchase",
  async (purchaseData, { rejectWithValue }) => {
    try {
      const response = await apiServices.post(`${BASE_URL}`, purchaseData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get all purchases
export const getAllPurchases = createAsyncThunk(
  "purchases/getAllPurchases",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`${BASE_URL}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
// Get all purchases
export const getAllDuePurchasesForSupplierId = createAsyncThunk(
  "purchases/getAllDuePurchasesForSupplierId",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`${BASE_URL}/due-purchases/supplier/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get a purchase by ID
export const getPurchaseById = createAsyncThunk(
  "purchases/getPurchaseById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`${BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Update a purchase
export const updatePurchase = createAsyncThunk(
  "purchases/updatePurchase",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await apiServices.put(`${BASE_URL}/${id}`, updatedData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
// Update a purchase
export const updatePurchasePrice = createAsyncThunk(
  "purchases/updatePurchasePrice",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await apiServices.put(`${BASE_URL}/usp/${id}`, updatedData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Delete a purchase
export const deletePurchase = createAsyncThunk(
  "purchases/deletePurchase",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiServices.delete(`${BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Search purchases by S/N, IMEI, or Barcode
export const searchPurchases = createAsyncThunk(
  "purchases/searchPurchases",
  async (query, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`${BASE_URL}/search-purchases?query=${query}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Check reference number uniqueness
export const checkReferenceNumber = createAsyncThunk(
  "purchases/checkReferenceNumber",
  async (referenceNumber, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`${BASE_URL}/check-reference/${referenceNumber}`);
      return response.data; // { exists: true/false }
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get restock suggestions (Low Stock Queue)
export const fetchRestockSuggestions = createAsyncThunk(
  "purchases/fetchRestockSuggestions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiServices.get(`/stock/restock-suggestions`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);


// Redux Slice
const purchaseSlice = createSlice({
  name: "purchases",
  initialState: {
    purchases: [], // All purchases
    supplierDuePurchases: [], // supplierDuePurchases
    purchaseDetails: {}, // Details of a single purchase
    restockSuggestions: [], // Low stock suggestions grouped by supplier
    status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null, // Any API error
  },
  reducers: {
    resetPurchaseState: (state) => {
      state.purchases = [];
      state.purchaseDetails = {};
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Purchase
      .addCase(createPurchase.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createPurchase.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.purchases.push(action.payload);
      })
      .addCase(createPurchase.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Get All Purchases
      .addCase(getAllPurchases.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAllPurchases.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.purchases = action.payload;
      })
      .addCase(getAllPurchases.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Search Purchases
      .addCase(searchPurchases.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchPurchases.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.purchases = action.payload;
      })
      .addCase(searchPurchases.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // getAllDuePurchasesForSupplierId
      .addCase(getAllDuePurchasesForSupplierId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAllDuePurchasesForSupplierId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.supplierDuePurchases = action.payload;
      })
      .addCase(getAllDuePurchasesForSupplierId.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.supplierDuePurchases = [];
      })

      // Get Purchase by ID
      .addCase(getPurchaseById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getPurchaseById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.purchaseDetails = action.payload;
      })
      .addCase(getPurchaseById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update Purchase
      .addCase(updatePurchase.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updatePurchase.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.purchases.findIndex((purchase) => purchase._id === action.payload._id);
        if (index !== -1) {
          state.purchases[index] = action.payload;
        }
      })
      .addCase(updatePurchase.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Delete Purchase
      .addCase(deletePurchase.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deletePurchase.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.purchases = state.purchases.filter((purchase) => purchase._id !== action.meta.arg);
      })
      .addCase(deletePurchase.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // fetchRestockSuggestions
      .addCase(fetchRestockSuggestions.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchRestockSuggestions.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.restockSuggestions = action.payload;
      })
      .addCase(fetchRestockSuggestions.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { resetPurchaseState } = purchaseSlice.actions;

export default purchaseSlice.reducer;
